import { createContext } from 'react';

/**
 * PD-17041 — how a dashboard widget body is sized, as seen by a `Table`
 * rendered inside it. `WidgetContainer` provides it around the widget body
 * only (not the fullscreen view), and a Table provides `null` to any table
 * nested inside its own rows, so only a report's top-level table reacts.
 *
 * - `fixed-cell`: the desktop grid. The widget is a fixed-height cell and its
 *   body scrolls vertically when the content does not fit. A table keeps the
 *   cell's remaining height and scrolls its own rows (the PD-14198 single
 *   scroller), but it reports whether its rows are shorter or taller than
 *   `minVisibleHeight` so the widget body can stop it being squeezed to
 *   nothing by the KPI cards and buttons above it (see `WidgetContainer`).
 * - `fit-content`: the phone stack. The widget grows to fit its content and
 *   the page does all the vertical scrolling, so a table renders every row of
 *   its page at natural height (`verticalScroll="page"`) instead of becoming
 *   a scroll box that a swipe gets trapped in.
 */
export type WidgetTableLayout =
	| { kind: 'fixed-cell'; minVisibleHeight: number }
	| { kind: 'fit-content' };

export const WidgetTableLayoutContext = createContext<WidgetTableLayout | null>(
	null,
);

/**
 * Smallest height a widget table is given on the desktop grid before the
 * widget body scrolls instead: the column header, the pager and about three
 * rows. Below this a table's rows are effectively unreachable.
 */
export const WIDGET_TABLE_MIN_VISIBLE_HEIGHT = 180;

/** Whether a table's rows fit under the floor ('short') or need more ('tall'). */
export type WidgetTableFit = 'short' | 'tall';

/**
 * The vertical scroll owner for a Table. An explicit prop always wins; inside
 * a fit-content (phone) widget the default becomes 'page'; everywhere else it
 * stays 'container', the Table's long-standing default.
 */
export function resolveTableVerticalScroll(
	explicit: 'container' | 'page' | undefined,
	widgetLayout: WidgetTableLayout | null,
): 'container' | 'page' {
	if (explicit != null) {
		return explicit;
	}
	return widgetLayout?.kind === 'fit-content' ? 'page' : 'container';
}

/**
 * The height a table's outer box would have if every row of the current page
 * were shown. Built from parts rather than from the box's current height,
 * because a squeezed table (the case this exists for) can be shorter than its
 * own pager, which then overflows the box and would be missed.
 */
export function naturalTableHeight(measurements: {
	/**
	 * Borders, plus any horizontal scrollbar, of the boxes around the rows:
	 * offsetHeight − clientHeight of each, summed.
	 */
	frameHeight: number;
	/** The pager under a paginated table; 0 when there is none. */
	pagerHeight: number;
	/** The full height of the rows (the `<table>` element). */
	contentHeight: number;
}): number {
	return (
		Math.max(0, measurements.frameHeight) +
		Math.max(0, measurements.pagerHeight) +
		Math.max(0, measurements.contentHeight)
	);
}

/**
 * A table whose rows fit under the floor is 'short': the widget body only has
 * to keep the table's own natural height on screen (holding it at the full
 * floor would add blank space and a scrollbar to a table that already fits).
 * Anything taller is 'tall' and is held at the floor, scrolling the rest.
 */
export function classifyWidgetTableFit(
	naturalHeight: number,
	minVisibleHeight: number,
): WidgetTableFit {
	return naturalHeight <= minVisibleHeight ? 'short' : 'tall';
}
